import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowRight, UserPlus, SlidersHorizontal, Users } from 'lucide-react';

// --- Live Activity Engine (profession-specific, action-based) ---
const CORE_PROFESSIONS = [
  'Architect', 'Civil Engineer', 'Structural Engineer', 'Electrical Engineer',
  'Mechanical Engineer', 'Quantity Surveyor', 'Project Manager', 'Site Supervisor',
  'Builder', 'Construction Manager',
];
const EXTENDED_PROFESSIONS = [
  'Draftsman', 'CAD Designer', 'BIM Specialist', 'Building Technologist',
  'MEP Engineer', 'HVAC Engineer', 'Site Engineer', 'Foreman', 'Technician', 'Plumber',
];
const ACTIONS = ['Joined', 'Registered', 'Verified', 'Shortlisted', 'Selected', 'Assigned', 'Engaged', 'Deployed'];

const NIGERIA_LOCATIONS = [
  'Lagos', 'Abuja', 'Port Harcourt', 'Delta State', 'Enugu', 'Kano', 'Kaduna',
  'Ibadan', 'Owerri', 'Uyo', 'Benin City', 'Abeokuta', 'Asaba', 'Warri',
  'Lekki', 'Victoria Island', 'Ikeja', 'Ajah', 'Gwarinpa', 'Wuse', 'Maitama', 'GRA Port Harcourt',
];
const AFRICA_LOCATIONS = ['Accra', 'Nairobi', 'Kigali', 'Kampala', 'Johannesburg', 'Abidjan', 'Dakar', 'Addis Ababa'];

const TIMES = ['Just now', '2 mins ago', '5 mins ago', '7 mins ago', '12 mins ago', '25 mins ago', '40 mins ago', '1 hour ago'];

function pickRandom(arr, exclude) {
  const filtered = exclude != null ? arr.filter(x => x !== exclude) : arr;
  return filtered[Math.floor(Math.random() * filtered.length)];
}

function generateActivity(buffer) {
  const last = buffer.length > 0 ? buffer[buffer.length - 1] : null;
  const profession = Math.random() < 0.8
    ? pickRandom(CORE_PROFESSIONS, last?.profession)
    : pickRandom(EXTENDED_PROFESSIONS, last?.profession);
  const action = pickRandom(ACTIONS, last?.action);
  const location = Math.random() < 0.7
    ? pickRandom(NIGERIA_LOCATIONS, last?.location)
    : pickRandom(AFRICA_LOCATIONS, last?.location);
  const time = pickRandom(TIMES, last?.time);
  const sentence = `${profession} ${action} \u2014 ${location} \u2014 ${time}`;
  if (buffer.some(b => b.sentence === sentence)) return generateActivity(buffer);
  return { profession, action, location, time, sentence };
}

const useActivityFeed = (minDelay, maxDelay) => {
  const [activity, setActivity] = useState('');
  const bufferRef = useRef([]);
  const next = useCallback(() => {
    const entry = generateActivity(bufferRef.current);
    bufferRef.current = [...bufferRef.current.slice(-29), entry];
    setActivity(entry.sentence);
  }, []);
  useEffect(() => {
    next();
    let timeout;
    const schedule = () => {
      const delay = minDelay + Math.random() * (maxDelay - minDelay);
      timeout = setTimeout(() => { next(); schedule(); }, delay);
    };
    schedule();
    return () => clearTimeout(timeout);
  }, [next, minDelay, maxDelay]);
  return activity;
};

const LiveActivityBanner = () => {
  const activity = useActivityFeed(2500, 4000);
  return (
    <AnimatePresence mode="wait">
      <motion.p key={activity} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3 }}
        className="text-sm text-slate-300 tracking-wide">
        {activity}
      </motion.p>
    </AnimatePresence>
  );
};

// --- Profession Groups (structured column layout) ---
const PROFESSION_GROUPS = [
  { title: 'Design & Technical', items: ['Architect', 'Draftsman', 'CAD Designer', 'BIM Specialist', 'Building Technologist'] },
  { title: 'Engineering', items: ['Civil Engineer', 'Structural Engineer', 'Mechanical Engineer', 'Electrical Engineer', 'MEP Engineer', 'HVAC Engineer'] },
  { title: 'Project & Cost', items: ['Quantity Surveyor', 'Project Manager', 'Construction Manager'] },
  { title: 'Site & Execution', items: ['Builder', 'Site Engineer', 'Site Supervisor', 'Foreman', 'Technician'] },
  { title: 'Specialized', items: ['Plumber'] },
];

// --- Toggle content (text replaceable; visual layout fixed) ---
const TOGGLES = [
  {
    icon: UserPlus,
    title: 'Get Project Opportunities',
    subtitle: 'Architects · Engineers · Project Managers · Other Core Building Professionals',
    description: 'Submit your CV & portfolio to get discovered across Africa.',
    button: 'SUBMIT CV & PORTFOLIO',
    image: 'https://customer-assets.emergentagent.com/job_submission-hub-21/artifacts/kixq6ddn_1000287724.png',
    imagePos: 'center 30%',
    to: '/submit',
    testId: 'submit-profile-button',
  },
  {
    icon: SlidersHorizontal,
    title: 'Manage Profile & Requests',
    subtitle: 'Professionals · Contractors · Construction Companies · Developers · Project Owners · Africans Abroad',
    description: 'Update your profile, manage requests and track activity across the BuildDirect Careers ecosystem.',
    button: 'MANAGE PROFILE & REQUESTS',
    image: 'https://customer-assets.emergentagent.com/job_submission-hub-21/artifacts/mwn4245s_1000287723.png',
    imagePos: 'center 40%',
    to: '/retrieve',
    testId: 'manage-profile-button',
  },
  {
    icon: Users,
    title: 'Build Your Project Team',
    subtitle: 'Contractors · Construction Companies · Developers · Project Owners · Africans Abroad',
    description: 'Request verified construction professionals for your projects across Africa.',
    button: 'REQUEST VERIFIED CONSTRUCTION PROFESSIONALS',
    image: 'https://customer-assets.emergentagent.com/job_submission-hub-21/artifacts/jwgifwbw_1000287735.png',
    imagePos: 'center 35%',
    to: '/request-professional',
    testId: 'request-professional-button',
  },
];

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-slate-200 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0B0F1A]/90 backdrop-blur-xl border-b border-slate-800/60 h-12 flex items-center justify-center px-6">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <img
            src="https://customer-assets.emergentagent.com/job_submission-hub-21/artifacts/1pp9pqr0_1000260687.png"
            alt="BuildDirect Careers"
            className="h-8 opacity-90"
          />
        </div>
      </header>

      {/* HERO — compact */}
      <section className="relative overflow-hidden pt-8 pb-6 sm:pt-10 sm:pb-8 px-6">
        <div className="absolute inset-0 -z-10 opacity-50 pointer-events-none" aria-hidden="true"
          style={{
            background:
              'radial-gradient(55% 45% at 50% 0%, rgba(37,99,235,0.18) 0%, rgba(37,99,235,0.06) 45%, transparent 75%), radial-gradient(35% 25% at 85% 15%, rgba(14,165,233,0.10) 0%, transparent 70%), radial-gradient(28% 22% at 15% 10%, rgba(99,102,241,0.08) 0%, transparent 70%)'
          }}
        />
        <div className="relative max-w-3xl mx-auto text-center space-y-2">
          <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="font-heading text-xl sm:text-2xl lg:text-3xl tracking-tight text-slate-100 leading-[1.25] font-semibold">
            Africa’s Construction Workforce<br className="hidden sm:block" /> Deployment Infrastructure
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 }}
            className="text-sm text-slate-400 max-w-xl mx-auto">
            Connecting verified construction professionals to contractors, developers, project owners, and Africans abroad across Africa.
          </motion.p>
        </div>
      </section>

      {/* THREE TOGGLE CARDS (text content; layout preserved) */}
      <section className="px-4 sm:px-6 pb-10">
        <div className="max-w-5xl mx-auto space-y-3 sm:space-y-4">
          {TOGGLES.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.button
                key={t.testId}
                type="button"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 + i * 0.08 }}
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.995 }}
                onClick={() => navigate(t.to)}
                data-testid={t.testId}
                aria-label={t.title}
                className="group relative block w-full text-left rounded-2xl overflow-hidden border border-blue-900/40 hover:border-blue-500/70 hover:shadow-[0_20px_60px_-16px_rgba(37,99,235,0.45)] focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500 transition-all duration-300 bg-[#0E1525]"
              >
                <div className="flex flex-col sm:flex-row items-stretch">
                  {/* Image — top banner on mobile, right column on desktop. Pushed inward + soft-fade mask so edges blend seamlessly into the dark card background */}
                  <div className="order-first sm:order-last relative w-full sm:w-[40%] aspect-[16/10] sm:aspect-auto sm:h-auto sm:min-h-[180px] flex-shrink-0 overflow-hidden bg-[#0E1525] pt-4 sm:pt-5 px-3 sm:px-4 pb-2 sm:pb-3">
                    <img
                      src={t.image}
                      alt=""
                      loading={i === 0 ? 'eager' : 'lazy'}
                      style={{
                        WebkitMaskImage:
                          'radial-gradient(ellipse 75% 80% at center, #000 55%, rgba(0,0,0,0.65) 75%, rgba(0,0,0,0) 100%)',
                        maskImage:
                          'radial-gradient(ellipse 75% 80% at center, #000 55%, rgba(0,0,0,0.65) 75%, rgba(0,0,0,0) 100%)',
                      }}
                      className="relative w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                  {/* Text — compact */}
                  <div className="flex-1 p-4 sm:p-6 flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0 pr-10">
                      <h3 className="text-base sm:text-lg font-bold text-slate-100 tracking-tight leading-snug">
                        {t.title}
                      </h3>
                      <p className="text-[11px] sm:text-xs text-blue-300/90 mt-1 leading-snug">
                        {t.subtitle}
                      </p>
                      <p className="text-[13px] sm:text-sm text-slate-400 mt-2 leading-snug">
                        {t.description}
                      </p>
                      <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 bg-blue-600 group-hover:bg-blue-500 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider px-3.5 py-2 sm:px-4 sm:py-2.5 rounded-lg shadow-md shadow-blue-600/20 transition-colors">
                        {t.button}
                        <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Circular proceed arrow (unchanged position + style) */}
                <div className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-300 group-hover:bg-blue-600 group-hover:text-white group-hover:translate-x-0.5 transition-all z-10">
                  <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* PROFESSIONS — dark column blocks */}
      <section className="bg-[#0E1525] border-y border-slate-800/60 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 space-y-2">
            <h2 className="font-heading text-xl sm:text-2xl tracking-tight text-slate-100 font-bold">
              Construction Professionals Across Key Disciplines
            </h2>
            <p className="text-sm text-slate-400 max-w-2xl mx-auto">
              BuildDirect Careers connects projects with verified professionals across design, engineering, cost, site execution, and technical construction roles.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {PROFESSION_GROUPS.map((group, gi) => (
              <motion.div
                key={group.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: gi * 0.05 }}
                data-testid={`profession-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="bg-[#0B0F1A] border border-slate-800/80 rounded-xl p-5 hover:border-blue-500/50 hover:shadow-[0_8px_24px_-8px_rgba(37,99,235,0.35)] transition-all duration-200"
              >
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-800">
                  <span className="w-1 h-4 bg-blue-500 rounded-full" />
                  <h3 className="text-[11px] uppercase tracking-[0.15em] text-blue-400 font-bold">
                    {group.title}
                  </h3>
                </div>
                <ul className="space-y-2">
                  {group.items.map(item => (
                    <li key={item} className="text-sm text-slate-300 leading-snug">
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 italic mt-8">
            + More core building professionals being onboarded
          </p>
        </div>
      </section>

      {/* LIVE ACTIVITY */}
      <section className="py-6 px-6 bg-[#070A12] border-t border-slate-800/60">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Activity className="h-3.5 w-3.5 text-green-400" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Live Activity</span>
          </div>
          <div className="h-6">
            <LiveActivityBanner />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-6 px-6 bg-[#0B0F1A] border-t border-slate-800/60 mt-auto">
        <div className="max-w-7xl mx-auto text-center space-y-1">
          <p className="text-[11px] text-slate-500">&copy; 2026 BuildDirect Africa Limited. All rights reserved.</p>
          <p className="text-[11px] text-slate-400">
            <a href="mailto:careers@builddirectcareers.com" className="text-blue-400 hover:text-blue-300 hover:underline">careers@builddirectcareers.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
